import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { Box, Button, CircularProgress, Chip } from '@mui/material';
import { Upload, Calculate } from '@mui/icons-material';
import { getSupabaseClient } from '@lingaai/supabase-client';

interface CashflowRow {
  id: string;
  section: 'actual' | 'projected' | 'metrics';
  date: string | null;
  description: string;
  type: 'capital_call' | 'distribution' | 'nav' | 'metric';
  amount: number | null;
  source: 'Actual' | 'Scenario';
  editable: boolean;
  metric_name?: string;
  actual_value?: number;
  projected_value?: number;
  delta?: number;
}

interface Metrics {
  xirr: number | null;
  tvpi: number | null;
  dpi: number | null;
  rvpi: number | null;
}

interface VehicleScenarioGridProps {
  vehicleId: string;
  vehicleName: string;
  initialProjectedCashflows?: CashflowRow[];
  onMetricsUpdate: (metrics: Metrics) => void;
  onCashflowsChange: (cashflows: CashflowRow[]) => void;
  onCalculatingChange: (isCalculating: boolean) => void;
  onSaveScenario: () => void;
  isActualTab?: boolean;
}

export const VehicleScenarioGrid = ({
  vehicleId,
  initialProjectedCashflows = [],
  onMetricsUpdate,
  onCashflowsChange,
  onCalculatingChange,
  onSaveScenario,
  isActualTab = false,
}: VehicleScenarioGridProps) => {
  const supabase = getSupabaseClient();
  const [rows, setRows] = useState<GridRowsProp<CashflowRow>>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [vehicleId]);

  // Reload when initialProjectedCashflows changes (tab switch)
  useEffect(() => {
    if (rows.length > 0) {
      const actualRows = rows.filter((r) => r.section === 'actual');
      setRows([...actualRows, ...initialProjectedCashflows]);
    }
  }, [JSON.stringify(initialProjectedCashflows)]);

  useEffect(() => {
    const projectedRows = rows.filter((r) => r.section === 'projected');
    onCashflowsChange(projectedRows);
  }, [rows]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('calculate-fund-metrics', {
        body: { vehicle_id: vehicleId },
      });

      if (error) throw error;

      const actualRows: CashflowRow[] = data.cashflows.map((cf: any, idx: number) => ({
        id: `actual-${idx}`,
        section: 'actual' as const,
        date: cf.cashflow_date,
        description: cf.description,
        type: cf.cashflow_type,
        amount: cf.amount,
        source: 'Actual' as const,
        editable: false,
      }));

      setRows([...actualRows, ...initialProjectedCashflows]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'section',
      headerName: 'Section',
      width: 130,
      renderCell: (params) => {
        const colors: Record<string, 'default' | 'primary' | 'success'> = {
          actual: 'default',
          projected: 'primary',
          metrics: 'success',
        };
        return <Chip label={params.value} size="small" color={colors[params.value]} />;
      },
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 130,
      type: 'date',
      editable: true,
      valueGetter: (value) => value && new Date(value),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      editable: true,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => {
        const labels: Record<string, string> = {
          capital_call: 'Capital Call',
          distribution: 'Distribution',
          nav: 'NAV',
          metric: 'Metric',
        };
        return labels[params.value] || params.value;
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      type: 'number',
      editable: true,
      valueFormatter: (value) => {
        if (value === null) return '';
        return `$${(value / 1000000).toFixed(1)}M`;
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
    },
  ];

  const processRowUpdate = async (newRow: CashflowRow) => {
    if (newRow.section !== 'projected') {
      return rows.find((r) => r.id === newRow.id)!;
    }
    setRows(rows.map((row) => (row.id === newRow.id ? newRow : row)));
    return newRow;
  };

  const handleCalculate = async () => {
    onCalculatingChange(true);
    try {
      const projectedRows = rows.filter((r) => r.section === 'projected');

      const navAdjustments = projectedRows.filter((r) => r.type === 'nav');
      const lastNavAdjustment = navAdjustments.length > 0 ? navAdjustments[navAdjustments.length - 1] : null;
      const navAdjustment = lastNavAdjustment ? lastNavAdjustment.amount || 0 : undefined;

      const { data, error } = await supabase.functions.invoke('calculate-fund-metrics', {
        body: {
          vehicle_id: vehicleId,
          scenario_overrides: {
            projected_exits: projectedRows
              .filter((r) => r.type === 'distribution')
              .map((r) => ({
                investment_id: 'scenario',
                exit_date: r.date || '',
                exit_value: r.amount || 0,
              })),
            future_calls: projectedRows
              .filter((r) => r.type === 'capital_call')
              .map((r) => ({
                call_date: r.date || '',
                call_amount: Math.abs(r.amount || 0),
              })),
            nav_adjustment: navAdjustment,
          },
          cache_result: false,
        },
      });

      if (error) throw error;

      onMetricsUpdate({
        xirr: data.metrics.xirr,
        tvpi: data.metrics.tvpi,
        dpi: data.metrics.dpi,
        rvpi: data.metrics.rvpi,
      });
      onCalculatingChange(false);
    } catch (error) {
      console.error('Error calculating metrics:', error);
      onCalculatingChange(false);
    }
  };

  const handleAddProjectedRow = () => {
    const newRow: CashflowRow = {
      id: `projected-${Date.now()}`,
      section: 'projected',
      date: new Date().toISOString().split('T')[0] as string,
      description: 'New Projection',
      type: 'distribution',
      amount: 0,
      source: 'Scenario',
      editable: true,
      metric_name: undefined,
      actual_value: undefined,
      projected_value: undefined,
      delta: undefined,
    };
    setRows([...rows, newRow]);
  };

  const handleAddNAVAdjustment = () => {
    const newRow: CashflowRow = {
      id: `projected-nav-${Date.now()}`,
      section: 'projected',
      date: new Date().toISOString().split('T')[0] as string,
      description: 'NAV Adjustment',
      type: 'nav',
      amount: 0,
      source: 'Scenario',
      editable: true,
      metric_name: undefined,
      actual_value: undefined,
      projected_value: undefined,
      delta: undefined,
    };
    setRows([...rows, newRow]);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" startIcon={<Upload />} onClick={handleAddProjectedRow}>
          Add Projection
        </Button>
        <Button variant="outlined" size="small" onClick={handleAddNAVAdjustment}>
          Adjust NAV
        </Button>
        <Button variant="contained" size="small" startIcon={<Calculate />} onClick={handleCalculate}>
          Calculate
        </Button>
        <Button variant="contained" size="small" color="success" onClick={onSaveScenario}>
          {isActualTab ? 'Save As New' : 'Save'}
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        isCellEditable={(params) => params.row.editable}
        initialState={{
          sorting: {
            sortModel: [{ field: 'section', sort: 'asc' }],
          },
          pagination: {
            paginationModel: { pageSize: 100 },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        rowHeight={32}
        columnHeaderHeight={32}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell': {
            border: '1px solid #e0e0e0',
            padding: '2px 8px',
          },
          '& .MuiDataGrid-columnHeaders': {
            border: '1px solid #e0e0e0',
            minHeight: '32px !important',
            maxHeight: '32px !important',
          },
          '& .MuiDataGrid-columnHeader': {
            border: '1px solid #e0e0e0',
            padding: '2px 8px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.875rem',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row': {
            minHeight: '32px !important',
            maxHeight: '32px !important',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#e3f2fd',
          },
        }}
      />
    </Box>
  );
};
